import { useEffect, useSyncExternalStore } from 'react';

import { errorMessage, fetchWithRetry, responseError } from './apiError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PREFERRED_MODEL_KEY = 'preferred_model';
const KNOWN_MODELS_KEY = 'model_catalog_ids';

/**
 * Static fallback for the featured models: ids match the backend registry
 * (mostlyagent/common/services/model_catalog.py). The full catalogue (~100
 * models) is loaded from GET /api/v1/public/models via `usePublicModels()`.
 */
export const MODEL_OPTIONS = [
  { value: 'dopamine-ultra', label: 'Dopamine Ultra 1' },
  { value: 'gpt-6-astra', label: 'GPT 6 Astra' },
  { value: 'fable-5.1', label: 'Fable 5.1' },
  { value: 'deepseek-v4-pro', label: 'Deepseek v4 Pro' },
] as const;

export type FeaturedModelId = typeof MODEL_OPTIONS[number]['value'];

/** Any id in the model registry; the static MODEL_OPTIONS are only the featured fallback. */
export type ModelId = string;

export const DEFAULT_MODEL: ModelId = 'gpt-6-astra';

export type ModelKind = 'plain' | 'escalation' | 'mapreduce';

export interface ModelPricing {
  currency: string;
  unit: string;
  input: number;
  output: number;
}

/**
 * What the model costs on the open market (OpenRouter), USD per million tokens. Models sold only
 * on 8080.AI carry a comparable model's rate instead, named in `compared_to`.
 */
export interface MarketPricing {
  source: string;
  model: string;
  input: number;
  output: number;
  /** False when OpenRouter could not be reached and the last recorded rate is shown. */
  live: boolean;
  compared_to?: string | null;
}

export interface PublicModel {
  id: string;
  name: string;
  provider: string;
  tagline: string;
  description: string;
  strengths: string[];
  is_default: boolean;
  featured: boolean;
  chat_available: boolean;
  api_available: boolean;
  kind: ModelKind;
  probe_model: string | null;
  fallback_model: string;
  /** Map-reduce wrappers: the model that answers, and how much it takes in one call. */
  base_model_id?: string | null;
  base_context_length?: number | null;
  context_length: number | null;
  rank: number | null;
  pricing: ModelPricing | null;
  market_pricing?: MarketPricing | null;
}

const LEGACY_IDS: Record<string, ModelId> = { 'dopamine-ultra-1': 'dopamine-ultra' };

/* ── Known ids ───────────────────────────────────────────────────────────
 * The featured ids are always known; every id the catalogue returns is added
 * and cached in localStorage so a remembered non-featured model survives a
 * reload even before the catalogue request completes. */

const readCachedIds = (): string[] => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KNOWN_MODELS_KEY) || '[]');

    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

let knownIds: Set<string> | null = null;

const known = (): Set<string> => {
  if (!knownIds) knownIds = new Set<string>([...MODEL_OPTIONS.map((option) => option.value), ...readCachedIds()]);

  return knownIds;
};

export const registerModelIds = (ids: Iterable<string>): void => {
  const set = known();

  Array.from(ids).forEach((id) => set.add(id));
  try {
    localStorage.setItem(KNOWN_MODELS_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* storage unavailable: ids only live for this page */
  }
};

export const isModelId = (value: unknown): value is ModelId => (
  typeof value === 'string' && known().has(value)
);

/* ── Shared catalogue store ──────────────────────────────────────────────── */

interface CatalogueSnapshot {
  models: PublicModel[] | null;
  error: string | null;
  loading: boolean;
}

const EMPTY: CatalogueSnapshot = { models: null, error: null, loading: false };
let snapshot: CatalogueSnapshot = EMPTY;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const emit = (next: CatalogueSnapshot) => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => { listeners.delete(listener); };
};

const getSnapshot = () => snapshot;

const FEATURED_IDS = new Set<string>(MODEL_OPTIONS.map((option) => option.value));

/** Fills the registry fields older backends omit so every consumer sees one shape. */
export const normalizePublicModel = (raw: Partial<PublicModel> & { id: string }): PublicModel => ({
  id: raw.id,
  name: raw.name ?? raw.id,
  provider: raw.provider ?? '8080.AI',
  tagline: raw.tagline ?? '',
  description: raw.description ?? '',
  strengths: Array.isArray(raw.strengths) ? raw.strengths : [],
  is_default: raw.is_default ?? raw.id === DEFAULT_MODEL,
  featured: raw.featured ?? FEATURED_IDS.has(raw.id),
  chat_available: raw.chat_available ?? true,
  api_available: raw.api_available ?? false,
  kind: raw.kind ?? 'plain',
  probe_model: raw.probe_model ?? null,
  fallback_model: raw.fallback_model ?? raw.id,
  base_model_id: raw.base_model_id ?? null,
  base_context_length: raw.base_context_length ?? null,
  context_length: raw.context_length ?? null,
  rank: raw.rank ?? null,
  pricing: raw.pricing ?? null,
  market_pricing: raw.market_pricing ?? null,
});

export const fetchPublicModels = async (): Promise<PublicModel[]> => {
  const response = await fetchWithRetry(`${API_URL}/api/v1/public/models`);

  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as { data?: (Partial<PublicModel> & { id: string })[] };

  return (payload.data ?? []).filter((item) => item && typeof item.id === 'string').map(normalizePublicModel);
};

/** Fetches the catalogue once and shares it with every subscriber; failures fall back to MODEL_OPTIONS. */
export const loadPublicModels = (): Promise<void> => {
  if (snapshot.models) return Promise.resolve();
  if (!inflight) {
    emit({ ...snapshot, loading: true });
    inflight = fetchPublicModels()
      .then((models) => {
        registerModelIds(models.map((model) => model.id));
        emit({ models, error: null, loading: false });
      })
      .catch((error: unknown) => {
        emit({ models: null, error: errorMessage(error, 'The model list could not be loaded.'), loading: false });
      })
      .finally(() => { inflight = null; });
  }

  return inflight;
};

/** Test hook: forget the cached catalogue and known ids. */
export const resetPublicModelsCache = (): void => {
  snapshot = EMPTY;
  inflight = null;
  knownIds = null;
};

export const FALLBACK_MODELS: PublicModel[] = MODEL_OPTIONS.map((option, index) => ({
  id: option.value,
  name: option.label,
  provider: '8080.AI',
  tagline: '',
  description: '',
  strengths: [],
  is_default: option.value === DEFAULT_MODEL,
  featured: true,
  chat_available: true,
  api_available: true,
  kind: 'plain',
  probe_model: null,
  fallback_model: option.value,
  context_length: null,
  rank: index + 1,
  pricing: null,
}));

export interface PublicModelsState {
  /** Catalogue models, or the static featured fallback until the request succeeds. */
  models: PublicModel[];
  loaded: boolean;
  loading: boolean;
  error: string | null;
}

export const usePublicModels = (): PublicModelsState => {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => { void loadPublicModels(); }, []);

  return {
    models: state.models ?? FALLBACK_MODELS, loaded: state.models !== null, loading: state.loading, error: state.error,
  };
};

export interface ProviderGroup {
  provider: string;
  models: PublicModel[];
}

export const groupModelsByProvider = (models: PublicModel[]): ProviderGroup[] => {
  const groups = new Map<string, PublicModel[]>();

  models.forEach((model) => {
    const bucket = groups.get(model.provider) ?? [];

    bucket.push(model);
    groups.set(model.provider, bucket);
  });

  return Array.from(groups.entries())
    .map(([provider, items]) => ({ provider, models: items }))
    .sort((left, right) => left.provider.localeCompare(right.provider));
};

export const modelLabel = (modelId: string | null | undefined): string => (
  snapshot.models?.find((model) => model.id === modelId)?.name
    ?? MODEL_OPTIONS.find((option) => option.value === modelId)?.label
    ?? (modelId || 'Default model')
);

/** Raw stored preference (legacy ids mapped), without validating it against the catalogue. */
export const readStoredModelId = (): string | null => {
  try {
    const stored = localStorage.getItem(PREFERRED_MODEL_KEY);

    return stored ? LEGACY_IDS[stored] ?? stored : null;
  } catch {
    return null;
  }
};

/** Resolves a candidate id to a known model, defaulting to GPT 6 Astra. */
export const resolveModelId = (candidate: string | null | undefined): ModelId => (
  isModelId(candidate) ? candidate : DEFAULT_MODEL
);

/** The model the user last picked in any composer; shared by the landing box and the chat page. */
export const readPreferredModel = (): ModelId => resolveModelId(readStoredModelId());

export const savePreferredModel = (modelId: ModelId): void => {
  try {
    localStorage.setItem(PREFERRED_MODEL_KEY, modelId);
  } catch {
    /* storage unavailable: the choice just lives for this page */
  }
};
